// components/EditRoleModal.js
import React, {useEffect, useMemo} from 'react'
import {Form, Input, Modal, Switch} from 'antd'
import useTranslate from 'modules/local/useTranslate'

// PermissionRow — nhận value/onChange được inject bởi Form.Item
const PermissionRow = ({label, description, coming_soon, value, onChange, t}) => (
    <div className="p-3 flex items-center relative list-item">
        <div className="flex flex-col flex-1">
            <div className="font-medium tracking-wide">
                {label}
            </div>
            {description && (
                <div className="text-xs text-color-300 tracking-wider">
                    {description}
                </div>
            )}
        </div>
        <div className="flex flex-col items-end gap-2">
            <Switch
                size="small"
                disabled={!!coming_soon}
                checked={!!value}
                onChange={onChange}
                className="w-fit"
            />
            {coming_soon && (
                <span style={{ padding: '3px 8px' }} className="w-fit flex flex-center bg-yellow-50 text-2xs text-yellow-400 italic rounded-full">
                    {t('coming soon')}
                </span>
            )}
        </div>
    </div>
)

const EditRoleModal = ({item, schema, open, loading, onCancel, onSubmit}) => {
    const t = useTranslate()
    const [form] = Form.useForm()

    useEffect(() => {
        if (!open || !item) return
        const permValues = {}
        if (schema) {
            Object.entries(schema).forEach(([key, def]) => {
                permValues[key] = !!def.value
            })
        }
        form.setFieldsValue({
            name: item.name,
            active: !!item.active,
            ...permValues,
        })
    }, [open, item, schema, form])

    const handleOk = () => {
        form.validateFields().then((values) => {
            const {name, active, ...rest} = values
            const permissions = {}
            if (schema) {
                Object.keys(schema).forEach((key) => {
                    if (key in rest) permissions[key] = rest[key]
                })
            }
            onSubmit && onSubmit({
                name,
                active: active ? 1 : 0,
                permissions: JSON.stringify(permissions),
            })
        })
    }

    const handleCancel = () => {
        form.resetFields()
        if (onCancel) onCancel()
    }

    const handleAfterClose = () => {
        form.resetFields()
    }

    const groupedSchema = useMemo(() => {
        if (!schema) return {}
        return Object.entries(schema).reduce((acc, [key, def]) => {
            const group = def.group || 'other'
            if (!acc[group]) acc[group] = []
            acc[group].push({key, ...def})
            return acc
        }, {})
    }, [schema])

    return (
        <Modal
            className="custom-modal"
            open={open}
            title={
                <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                    {t('edit role')}
                </div>
            }
            okText={t('save')}
            cancelText={t('cancel')}
            onOk={handleOk}
            onCancel={handleCancel}
            afterClose={handleAfterClose}
            confirmLoading={loading}
            width={560}
            destroyOnHidden>

            <Form form={form} layout="vertical">

                {/* ── Basic info card ── */}
                <div className="mb-6 p-4 background-50 rounded-xl space-y-6">
                    {/* Tên vai trò */}
                    <Form.Item
                        name="name"
                        label={
                            <span className="text-sm font-medium text-color-200 tracking-wider pr-3">
                                {t('role name')}
                            </span>
                        }
                        rules={[
                            {required: true, message: t('role name is required')},
                            {min: 2, message: t('min 2 characters')},
                            {max: 100, message: t('max 100 characters')},
                        ]}
                        className="mb-4">
                        <Input autoComplete="off" className="rounded-lg"/>
                    </Form.Item>
                    {/* Trạng thái hoạt động — dùng noStyle để tránh double Form.Item */}
                    <div className="flex items-center justify-between pb-3">
                        <span className="text-sm font-medium text-color-200 tracking-wider pr-3">
                            {t('active')}
                        </span>
                        <Form.Item name="active" valuePropName="checked" noStyle>
                            <Switch/>
                        </Form.Item>
                    </div>
                </div>

                {/* ── Permissions nhóm theo group ── */}
                {Object.entries(groupedSchema).map(([group, items]) => (
                    <div key={group} className="mb-4">
                        <div className="mb-2 font-semibold text-slate-600 uppercase tracking-widest">
                            {t(group)}
                        </div>
                        <div className="border border-color-50 rounded-xl divide-y divide-gray-100 list-item-workspace">
                            {items.map(({key, label, description, coming_soon}) => (
                                <Form.Item
                                    key={key}
                                    name={key}
                                    valuePropName="checked"
                                    getValueFromEvent={(checked) => checked}
                                    noStyle>
                                    <PermissionRow
                                        label={label}
                                        description={description}
                                        coming_soon={coming_soon}
                                        t={t}
                                    />
                                </Form.Item>
                            ))}
                        </div>
                    </div>
                ))}

            </Form>
        </Modal>
    )
}

export default EditRoleModal